import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, Phone, Mic, MessageCircle } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { createBreadcrumbSchema, createCollectionPageSchema, createOrganizationSchema } from '@/config/seo';
import { PlaybookService } from '@/services/playbooks/PlaybookService';
import { SolutionFinalCTA } from '@/pages/Solutions/components/SolutionFinalCTA';
import HomeNav from '../Home/HomeNav';
import HomeFooter from '../Home/HomeFooter';
import { getCanonicalUrl } from '@/config/site';
import { cn } from '@/lib/utils';
import { getPlaybookImage, categoryHeroImages } from '@/pages/Playbooks/playbook-images';

// Ken Burns animation variants
const kenBurnsVariants = [
  { scale: [1, 1.04], x: [0, -8], y: [0, -5] },
  { scale: [1, 1.05], x: [0, 8], y: [0, -4] },
  { scale: [1, 1.03], x: [0, -5], y: [0, 6] },
  { scale: [1, 1.04], x: [0, 6], y: [0, 4] },
];

const rotatingWords = ['convert', 'support', 'engage', 'grow'];

// CTA Pillars
const ctaPillars = [
  {
    title: '24/7 Voice\nSupport',
    gradient: 'from-[#4E9CFF] to-[#7BBDFF]',
    description: 'Your genie never sleeps, never takes breaks, and always sounds great.',
  },
  {
    title: 'Convert More\nLeads',
    gradient: 'from-[#FF4DB8] to-[#FF80D0]',
    description: 'Capture and qualify leads through natural conversation, automatically.',
  },
  {
    title: 'Scale Without\nHiring',
    gradient: 'from-[#FFBB33] to-[#FFD580]',
    description: 'Handle thousands of conversations simultaneously without adding headcount.',
  },
];

export default function PlaybooksLanding() {
  const canonicalUrl = getCanonicalUrl('/playbooks');
  const [wordIndex, setWordIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);

  const categoriesWithPlaybooks = PlaybookService.getCategoriesWithPlaybooks();

  const structuredData = [
    createBreadcrumbSchema([
      { name: 'Home', url: getCanonicalUrl('/') },
      { name: 'Playbooks', url: canonicalUrl },
    ]),
    createCollectionPageSchema(
      'Voice AI Playbooks',
      'Browse Help Genie voice AI playbooks for support, lead capture, onboarding, physical experiences, marketing, and customer insight workflows.',
      canonicalUrl,
    ),
    createOrganizationSchema(),
  ];

  // Rotating words
  useEffect(() => {
    const timer = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  // Ken Burns
  useEffect(() => {
    const timer = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % kenBurnsVariants.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const currentWord = rotatingWords[wordIndex];

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F9FAFC]">
      <Helmet>
        <title>Voice AI Playbooks | Help Genie</title>
        <meta
          name="description"
          content="Browse Help Genie voice AI playbooks for lead capture, support, onboarding, marketing, physical experiences, and customer insight collection."
        />
        <meta
          name="keywords"
          content="voice ai playbooks, ai support playbooks, lead capture automation, customer support automation, conversational ai templates"
        />
        <link rel="canonical" href={canonicalUrl} />
        {structuredData.map((entry, index) => (
          <script key={`playbooks-landing-structured-data-${index}`} type="application/ld+json">
            {JSON.stringify(entry)}
          </script>
        ))}
      </Helmet>
      <HomeNav />

      <main className="flex-1 w-full">
        {/* Hero Section with Ken Burns */}
        <section className="relative w-full h-[65vh] sm:h-[78vh] md:h-[84vh] min-h-[480px] sm:min-h-[600px] md:min-h-[660px] flex items-center text-white overflow-hidden">
          <div className="absolute inset-0 z-0 overflow-hidden">
            <AnimatePresence mode="sync">
              <motion.img
                key={imageIndex}
                src="/playbooks/playbooks-landing-hero.webp"
                alt="Playbooks hero background"
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ opacity: 0, scale: 1 }}
                animate={{
                  opacity: 1,
                  scale: kenBurnsVariants[imageIndex].scale,
                  x: kenBurnsVariants[imageIndex].x,
                  y: kenBurnsVariants[imageIndex].y,
                }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: { duration: 1.2, ease: 'easeInOut' },
                  scale: { duration: 8, ease: 'linear' },
                  x: { duration: 8, ease: 'linear' },
                  y: { duration: 8, ease: 'linear' },
                }}
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-black/70 z-10" />
          </div>

          <div className="relative z-20 w-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-5xl">
              <h1 className="font-bold leading-none drop-shadow-2xl mb-6" style={{ fontSize: 'clamp(2.5rem, 6vw, 7rem)' }}>
                <div className="text-white">Your voice genie,</div>
                <div className="whitespace-nowrap">
                  <span className="text-white">ready to </span>
                  <span
                    key={currentWord}
                    className="bg-gradient-to-r bg-clip-text text-transparent animate-shimmer bg-[length:200%_100%]"
                    style={{
                      backgroundImage:
                        'linear-gradient(110deg, #4E9CFF 0%, #FF4DB8 35%, #FFBB33 65%, #FF4DB8 85%, #4E9CFF 100%)',
                    }}
                  >
                    {currentWord}
                  </span>
                </div>
              </h1>

              <p
                className="mt-6 text-white/80 font-normal drop-shadow-lg max-w-3xl mx-auto"
                style={{ fontSize: 'clamp(1.125rem, 1.75vw, 1.5rem)' }}
              >
                Pre-built conversation strategies for every business goal. Pick a playbook, deploy your genie, and let it handle conversations across phone, web chat, and SMS.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/signup"
                  className="rounded-lg bg-gradient-to-r from-[#4E9CFF] to-[#28406F] text-white font-semibold shadow-lg hover:from-[#3d8de6] hover:to-[#1f3459] transition-all hover:-translate-y-0.5 min-h-[48px] px-8 py-3.5 text-lg inline-flex items-center justify-center"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/live-agent/business-onboarding"
                  className="rounded-lg bg-white/10 text-white border-2 border-white/50 hover:bg-white/20 hover:border-white font-semibold shadow-lg backdrop-blur-sm transition-all hover:-translate-y-0.5 min-h-[48px] px-8 py-3.5 text-lg inline-flex items-center justify-center"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Talk To Me
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section - Clean 3-step layout */}
        <section className="w-full py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <div className="h-1 w-16 bg-gradient-to-r from-primary to-secondary rounded-full mx-auto mb-6" />
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                How Voice Playbooks Work
              </h2>
              <p className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto">
                Pick a playbook. Deploy your genie. Watch it work.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  step: '01',
                  title: 'Choose a Playbook',
                  description: 'Browse proven strategies for support, leads, onboarding, and more. Each one is ready to deploy.',
                },
                {
                  step: '02',
                  title: 'Deploy Your Genie',
                  description: 'One click creates a genie pre-configured with the playbook strategy, prompts, and conversation flow — ready for voice calls and text chat.',
                },
                {
                  step: '03',
                  title: 'Watch It Work',
                  description: 'Your genie handles conversations via phone, web chat, and SMS — capturing leads, answering questions, and achieving your goals 24/7.',
                },
              ].map((item, idx) => (
                <div key={idx} className="text-center group">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#4E9CFF] to-[#28406F] text-white text-xl font-bold mb-6 shadow-lg group-hover:scale-110 transition-transform">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Playbooks by Category */}
        <section className="py-20 md:py-32 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Choose Your Voice Strategy
              </h2>
              <p className="text-lg md:text-xl text-gray-500">
                Each playbook is a complete voice conversation strategy. Pick your goal and deploy your genie.
              </p>
            </div>

            <div className="space-y-24">
              {categoriesWithPlaybooks.map((catWithPlaybooks) => {
                const heroImage = categoryHeroImages[catWithPlaybooks.slug] || '/hero-background.webp';
                const CategoryIcon = catWithPlaybooks.icon;

                return (
                  <CategorySection
                    key={catWithPlaybooks.id}
                    category={catWithPlaybooks}
                    playbooks={catWithPlaybooks.playbooks}
                    heroImage={heroImage}
                    CategoryIcon={CategoryIcon}
                  />
                );
              })}
            </div>
          </div>
        </section>

        {/* Final CTA with Typewriter Pillars */}
        <SolutionFinalCTA
          title="Your Business Never Sleeps"
          subtitle="While you rest, your genies capture every lead, handle every question, and ensure no opportunity slips through the cracks."
          pillars={ctaPillars}
          backgroundImage="/hero-background.webp"
        />
      </main>

      <HomeFooter />

      {/* Shimmer animation styles */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

// --- Category Section Component ---

const CategorySection = ({
  category,
  playbooks,
  heroImage,
  CategoryIcon,
}: {
  category: { id: string; slug: string; name: string; description: string };
  playbooks: ReturnType<typeof PlaybookService.getAllPlaybooks>;
  heroImage: string;
  CategoryIcon: React.ComponentType<{ className?: string }>;
}) => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="scroll-mt-20" id={category.slug}>
      {/* Category Header with Hero Image */}
      <div
        className={cn(
          'mb-12 transition-all duration-700 ease-out',
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        )}
      >
        <div className="rounded-2xl shadow-lg bg-gradient-to-r from-genie-logo to-genie-primary p-8 lg:p-12">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white/20 rounded-2xl flex-shrink-0">
              <CategoryIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2">
                {category.name}
              </h3>
              <p className="text-white/80 text-lg max-w-2xl">
                {category.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Playbook Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {playbooks.map((playbook, idx) => (
          <div
            key={playbook.meta.id}
            className={cn(
              'bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer',
              isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
            style={{ transitionDelay: `${idx * 100 + 200}ms` }}
            onClick={() => {
              navigate(PlaybookService.getPlaybookUrl(playbook));
            }}
          >
            {/* Card Image */}
            <div className="relative h-40 overflow-hidden">
              <img
                src={getPlaybookImage(playbook.meta.id, category.slug)}
                alt={playbook.meta.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                onError={(e) => {
                  e.currentTarget.src = '/hero-background.webp';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm text-xs">
                  {category.name}
                </Badge>
              </div>
            </div>

            {/* Card Content */}
            <div className="p-6 space-y-3">
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-primary transition-colors">
                  {playbook.meta.name}
                </h4>
                <p className="text-sm font-medium text-primary">{playbook.meta.tagline}</p>
              </div>

              <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                {playbook.meta.strategy}
              </p>

              {/* Benefits pills */}
              {playbook.meta.benefits && playbook.meta.benefits.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {playbook.meta.benefits.slice(0, 2).map((benefit, bidx) => (
                    <span
                      key={bidx}
                      className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-full"
                    >
                      {benefit}
                    </span>
                  ))}
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Mic className="h-3 w-3" />
                    <MessageCircle className="h-3 w-3" />
                    Voice & Text
                  </span>
                </div>
                <ChevronRight className="h-5 w-5 text-primary group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
